/**
 * Ejecutar la siguiente pasada del plan (Fase 12, R-16).
 *
 * Antes de pulsar: qué pasada, qué capítulos, cuántas palabras y cuánto cuesta. Después: el build
 * nuevo con su control de cambios y qué hacer en Word. Si el motor para (documento abierto en
 * Word, cambios sin resolver), se dice con sus palabras y no se gasta nada.
 */
import { useEffect, useState } from "react";
import { PuertaPasada, capitulosDeTexto, costeDeLaPasada, documento, dolares, ejecutarPasada } from "../api";
import type { ModeloCatalogo, PasadaPlan, Trabajo } from "../api";
import { dentroDeCarpeta, estimarCoste, fraseProsaQuitada, frasesDelTablero, frasesSinTocar, mismoLibro, nombreDeRuta } from "../logica";
import type { SinTocar } from "../logica";
import { ListaDePasos } from "./ListaDePasos";
import { useFlujo } from "../flujo";

export default function EjecutarPasada({ siguiente: laQueToca, pendientes = [], total, soportado, carpeta, doc, base = null, trabajo, precio, alLanzar, alHablar, alIr, alSeguirCon }: {
  siguiente: (PasadaPlan & { area: string }) | null;
  pendientes?: (PasadaPlan & { area: string })[];
  total: number;
  soportado: boolean;
  carpeta: string;
  doc: string | null;
  /** Sobre qué manuscrito se escribió el plan: si no es el abierto, la pasada iría a otro libro. */
  base?: string | null;
  trabajo: Trabajo | null;
  precio: ModeloCatalogo | undefined;
  alLanzar: () => void;
  alHablar: () => void;
  alIr: (s: string) => void;
  /** La versión que acaba de nacer pasa a ser el manuscrito: la siguiente pasada sale de ella. */
  alSeguirCon?: (ruta: string) => void;
}) {
  const flujo = useFlujo();   // el Studio nuevo: las pasadas no se piden a nadie, se ejecutan
  const [palabras, setPalabras] = useState<number | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Un RECHAZO, no una nota: la pasada no se ha hecho y hay que hacer algo antes (Rais, 23-sep:
  // leyó el recuadro amarillo como un aviso y creyó que la pasada se había ejecutado).
  const [puerta, setPuerta] = useState<string | null>(null);
  // Cuando la puerta es «trabajas sobre una versión vieja», la salida es un botón, no un párrafo.
  const [laUltima, setLaUltima] = useState<string | null>(null);
  // 17-sep: el orden del plan recomienda, no obliga. Por defecto la que toca; se puede elegir otra.
  const [elegida, setElegida] = useState<number | null>(null);
  const siguiente = pendientes.find((p) => p.n === elegida) ?? laQueToca;
  const enCurso = trabajo?.estado === "en_curso";
  // La pasada escribe AL LADO del documento abierto. Si ese documento está fuera de la carpeta del
  // proyecto —o no es el manuscrito con el que se escribió el plan—, la versión nueva nacería en
  // otro sitio y sobre otro texto (Rais, 22-sep: la pasada iba a escribir sobre un docx de Descargas).
  const fuera = !!doc && !!carpeta && !dentroDeCarpeta(doc, carpeta);
  // La SERIE, no el nombre exacto: el plan se escribió sobre la v01 y la pasada 2 sale de la v02,
  // que es el mismo libro (Rais, 23-sep: el aviso apagaba el botón al avanzar de versión).
  const otroLibro = !!doc && !!base && !mismoLibro(doc, base);
  const paraTodos = fuera || otroLibro;
  const nums = Array.isArray(siguiente?.capitulos) ? siguiente!.capitulos as number[] : [];

  // Una pasada escrita en OPERACIONES no manda capítulos: el coste lo cuenta el motor sobre las
  // escenas que de verdad viajan (restar no cuesta nada). Por capítulos, la cifra no tenía relación
  // con lo que se cobra (equipo rojo 21-sep).
  const porOperaciones = Array.isArray((siguiente as { operaciones?: unknown[] } | undefined)?.operaciones)
    && ((siguiente as { operaciones?: unknown[] }).operaciones?.length ?? 0) > 0;
  useEffect(() => {
    if (!porOperaciones || !soportado || !doc || siguiente?.n == null) return;
    setPalabras(null);
    costeDeLaPasada(carpeta, doc, null, siguiente.n).then((r) => setPalabras(r.palabras)).catch(() => {});
  }, [porOperaciones, soportado, doc, siguiente?.n]);

  // Las palabras de los capítulos de la pasada, para el coste antes de pulsar.
  useEffect(() => {
    if (porOperaciones) return;
    setPalabras(null);
    if (!soportado || !doc || !nums.length) return;
    documento(doc).then((d) => ("error" in d ? null : capitulosDeTexto(d.text, carpeta, doc))).then((r) => {
      if (!r) return;
      const reales = r.capitulos.filter((c) => c.title !== "Preliminares");
      setPalabras(nums.reduce((n, i) => n + (reales[i - 1]?.palabras ?? 0), 0));
    }).catch(() => {});
  }, [soportado, doc, siguiente?.n]);

  async function ejecutar() {
    if (!doc) return;
    setOcupado(true); setPuerta(null);
    try { await ejecutarPasada(carpeta, doc, siguiente?.n ?? null); alLanzar(); }
    catch (e) {
      // «Trabajas sobre una versión vieja» no es algo que el autor tenga que arreglar: la pasada se
      // rehace sobre la última y punto (Rais, 23-sep: «esto lo tiene que hacer automáticamente»).
      if (e instanceof PuertaPasada && e.codigo === "no_es_la_ultima" && e.ultima && carpeta) {
        const ruta = `${carpeta}/${e.ultima}`;
        alSeguirCon?.(ruta);
        try { await ejecutarPasada(carpeta, ruta, siguiente?.n ?? null); alLanzar(); setPuerta(null); return; }
        catch (otra) { setPuerta(otra instanceof PuertaPasada ? otra.message : (otra as Error).message); return; }
      }
      setPuerta(e instanceof PuertaPasada ? e.message : (e as Error).message);
      setLaUltima(null);
    }
    finally { setOcupado(false); }
  }

  // LA VERSIÓN NUEVA PASA A SER EL MANUSCRITO (Rais, 23-sep: «he aceptado los cambios de la pasada
  // y no pasa nada»). La pasada escribe DMVM-m01-v02.docx y el Studio seguía apuntando a la v01, así
  // que la siguiente pasada habría salido del texto viejo — o habría chocado con un build existente.
  // Cuánto lleva: sin esto, una pasada de siete operaciones parece colgada al minuto (Rais, 23-sep).
  const desdeHace = (() => {
    if (!enCurso || !trabajo?.inicio) return "";
    const min = Math.max(0, Math.round((Date.now() - Date.parse(trabajo.inicio)) / 60000));
    const hora = new Date(trabajo.inicio).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    return `. Empezó a las ${hora}${min >= 1 ? ` y lleva ${min} ${min === 1 ? "minuto" : "minutos"}` : ""}.`;
  })();

  const nacida = trabajo?.estado === "hecho" ? String((trabajo.resultado as { build?: string })?.build ?? "") : "";
  const hayQueSeguirCon = nacida && doc && nombreDeRuta(doc) !== nacida ? nacida : null;
  useEffect(() => {
    if (!hayQueSeguirCon || !alSeguirCon || !carpeta) return;
    alSeguirCon(`${carpeta}/${hayQueSeguirCon}`);
  }, [hayQueSeguirCon, alSeguirCon, carpeta]);

  const coste = palabras != null ? estimarCoste(palabras, precio, undefined, "reescritura").coste : null;
  const hecho = trabajo?.estado === "hecho" ? trabajo.resultado as { build?: string; aplicadas?: number; delta?: number; pasada?: number; sin_ancla?: number; sin_tocar?: SinTocar[]; prosa_quitada?: { quitados?: number; devueltos?: number };
    en_tablero?: { propia: string | null; enlazadas: string[] } } : null;
  const enTablero = hecho?.en_tablero ? frasesDelTablero({ nota: hecho.en_tablero.propia, notas: hecho.en_tablero.enlazadas }) : null;

  if (!soportado) {
    return (
      <section className="carta">
        <h2>{siguiente ? `Pasada ${siguiente.n} de ${total} · ${siguiente.titulo}` : "La siguiente pasada"}</h2>
        <p className="vacio">
          Cada pasada crea una versión nueva con control de cambios: tu versión anterior no se toca. Con el motor 1.6.3
          se ejecuta desde aquí con el plan aprobado; con el tuyo, {flujo ? "actualiza el motor desde Ajustes" : "se pide al editor en el chat"}.
        </p>
        <div className="botones">
          <button className="btn" onClick={alHablar}>{flujo ? "Preguntar al coach" : "Pedírsela al editor"}</button>
          <button className="btn texto" onClick={() => alIr("plan")}>Ver el plan</button>
        </div>
      </section>
    );
  }

  return (
    <>
      {hecho && (
        <section className="carta">
          <h2>Pasada {hecho.pasada} hecha<span className="sub">{hecho.aplicadas} cambios</span></h2>
          <p className="vacio">
            Se ha creado <b>{hecho.build}</b> con los cambios marcados{hecho.delta ? ` (${hecho.delta > 0 ? "+" : ""}${hecho.delta.toLocaleString("es-ES")} palabras)` : ""}. Tu versión anterior sigue intacta.
          </p>
          {!!hecho.sin_ancla && (
            <p className="vacio" style={{ color: "var(--acc)" }}>
              {hecho.sin_ancla} {hecho.sin_ancla === 1 ? "cambio propuesto no encontró" : "cambios propuestos no encontraron"} su sitio en el documento y no se aplicaron. Si hacen falta, pídeselos al editor en el chat.
            </p>
          )}
          {frasesSinTocar(hecho.sin_tocar) && <p className="vacio" style={{ color: "var(--acc)" }}>{frasesSinTocar(hecho.sin_tocar)}</p>}
          {fraseProsaQuitada(hecho.prosa_quitada) && <p className="vacio">{fraseProsaQuitada(hecho.prosa_quitada)}</p>}
          {/* R0-6: esperan en «En Word, te esperan» y se cierran al leer lo que decidas. */}
          {enTablero && <p className="vacio">{enTablero} Te esperan en «En Word, te esperan», y se leen solas cuando guardes en Word lo que decidas.</p>}
          <ol className="pasos">
            <li>Abre <b>{hecho.build}</b> en Word.</li>
            <li>Acepta o rechaza los cambios (Revisar → Aceptar / Rechazar; puedes filtrar por autor).</li>
            <li>Guarda y vuelve aquí: elige esa versión como manuscrito y sigue con la siguiente pasada.</li>
          </ol>
        </section>
      )}
      <section className="carta">
        <h2>{siguiente ? `Pasada ${siguiente.n} de ${total} · ${siguiente.titulo}` : "Todas las pasadas del plan están hechas"}
          {siguiente && <span className="sub">{siguiente.area}</span>}
        </h2>
        {siguiente ? (
          <>
            <p className="vacio">
              {nums.length ? `${nums.length === 1 ? "Capítulo" : "Capítulos"} ${nums.join(", ")}` : "Capítulos del plan"}
              {siguiente.criterio_cierre ? ` · se da por cerrada cuando ${siguiente.criterio_cierre}` : ""}.
              La pasada se escribe sobre <b>{doc ? nombreDeRuta(doc) : "tu manuscrito"}</b> y crea una versión nueva con control de cambios.
            </p>
            {paraTodos && (
              <p className="aviso">
                {fuera
                  ? <>El documento abierto, <b>{nombreDeRuta(doc!)}</b>, <b>no está en la carpeta de tu proyecto</b>: la versión nueva
                     se crearía donde esté él, fuera del proyecto. </>
                  : <>El plan se escribió sobre <b>{base}</b> y el documento abierto es <b>{nombreDeRuta(doc!)}</b>: serían dos libros distintos. </>}
                Elige el manuscrito del proyecto en Manuscrito y vuelve; si de verdad quieres trabajar con este otro,
                súbelo antes al proyecto y rehaz el informe y el plan.
                <button className="btn texto" onClick={() => alIr("manuscrito")}>Ir a Manuscrito</button>
              </p>
            )}
            {trabajo && trabajo.estado !== "hecho" && <ListaDePasos trabajo={trabajo} />}
            {enCurso ? (
              <p className="vacio">
                {flujo ? "Se está reescribiendo" : "El editor está reescribiendo"}{trabajo?.progreso ? `: ${Math.min(trabajo.progreso.hecho + 1, trabajo.progreso.total)} de ${trabajo.progreso.total}` : "…"}
                {desdeHace}{" "}Cada paso es una llamada al modelo y tarda uno o dos minutos; puedes cerrar el Studio, sigue en tu ordenador.
              </p>
            ) : (
              <div className="botones">
                {pendientes.length > 1 && (
                  <select className="escribir chip" aria-label="Qué pasada ejecutar" value={siguiente.n} onChange={(e) => setElegida(Number(e.target.value))}>
                    {pendientes.map((p) => (
                      <option key={p.n} value={p.n}>{p.n === laQueToca?.n ? "Toca: " : ""}pasada {p.n} · {p.titulo}</option>
                    ))}
                  </select>
                )}
                <button className="btn" disabled={!doc || ocupado || paraTodos} onClick={ejecutar}>Ejecutar la pasada {siguiente.n}</button>
                <span className="vacio">
                  {palabras != null ? `~${palabras.toLocaleString("es-ES")} palabras` : ""}
                  {coste != null ? ` · ~${dolares(coste)} de tu llave` : ""}
                </span>
                <button className="btn texto" onClick={alHablar}>{flujo ? "Preguntar antes al coach" : "Hablarlo antes con el editor"}</button>
              </div>
            )}
          </>
        ) : (
          <div className="botones"><button className="btn" onClick={() => alIr("correccion")}>Ir a Corrección</button></div>
        )}
        {puerta && (
          <div className="aviso" style={{ borderColor: "var(--acc)" }}>
            <b style={{ color: "var(--acc)" }}>La pasada no se ha hecho.</b> {puerta}
            <div className="botones">
              <button className={laUltima ? "btn fantasma" : "btn"} disabled={ocupado} onClick={ejecutar}>{ocupado ? "Comprobando…" : "Ya lo he hecho · vuelve a intentarlo"}</button>
              {doc && (
                <button className="btn texto" onClick={() => { navigator.clipboard?.writeText(doc).catch(() => {}); }}>
                  Copiar la ruta del fichero
                </button>
              )}
            </div>
          </div>
        )}
        {trabajo?.estado === "fallido" && <p className="vacio" style={{ color: "var(--acc)" }}>La última pasada no se completó: {trabajo.error} No se ha creado ningún build.</p>}
        {/* Reiniciar el motor (una actualización, apagar el Mac) corta lo que estuviera haciendo:
            se dice y se vuelve a lanzar, en vez de dejar una pasada fantasma (Rais, 23-sep). */}
        {trabajo?.estado === "interrumpido" && (
          <p className="vacio" style={{ color: "var(--acc)" }}>
            La pasada se cortó al reiniciarse el motor y no ha creado ninguna versión. Vuelve a lanzarla: empieza donde estabas.
          </p>
        )}
      </section>
    </>
  );
}
